import type { ComponentProps } from "react"
import type { OrderAddress } from "@/api/account-order-detail"
import { cn } from "@/utilities"

function formatAddress(address: OrderAddress): string {
  const parts = [
    `${address.firstName} ${address.lastName}`,
    address.company,
    address.address1,
    address.address2,
    `${address.city}, ${address.state} ${address.postcode}`,
    address.country,
  ].filter(Boolean)

  return parts.join("\n")
}

interface AddressBlockProps extends ComponentProps<"div"> {
  title: string
  address: OrderAddress
}

function AddressBlock({
  title,
  address,
  className,
  ...props
}: AddressBlockProps) {
  return (
    <div className={cn("space-y-3", className)} {...props}>
      <h3 className='font-semibold text-foreground'>{title}</h3>
      <address className='whitespace-pre-line text-muted-foreground text-sm not-italic leading-relaxed'>
        {formatAddress(address)}
        {address.phone && (
          <>
            {"\n"}
            <a
              className='text-foreground hover:underline'
              href={`tel:${address.phone}`}
            >
              {address.phone}
            </a>
          </>
        )}
        {address.email && (
          <>
            {"\n"}
            <a
              className='text-foreground hover:underline'
              href={`mailto:${address.email}`}
            >
              {address.email}
            </a>
          </>
        )}
      </address>
    </div>
  )
}

interface CustomerInfoProps extends ComponentProps<"div"> {
  billingAddress: OrderAddress
  shippingAddress?: OrderAddress
  needsShipping: boolean
}

export function CustomerInfo({
  billingAddress,
  shippingAddress,
  needsShipping,
  className,
  ...props
}: CustomerInfoProps) {
  return (
    <div
      className={cn(
        "space-y-4 rounded-2xl border border-border bg-background p-6 shadow-xs",
        className
      )}
      {...props}
    >
      <h2 className='font-semibold text-foreground text-xl'>
        Informações de entrega
      </h2>

      {needsShipping && shippingAddress ? (
        <div className='grid gap-6 sm:grid-cols-2'>
          <AddressBlock address={billingAddress} title='Endereço de cobrança' />
          <AddressBlock address={shippingAddress} title='Endereço de entrega' />
        </div>
      ) : (
        <AddressBlock address={billingAddress} title='Endereço de cobrança' />
      )}
    </div>
  )
}
